import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { formsList } from '../utils/urls';

import { Forms } from 'types/forms';
import { ErrorModal, SortItemType } from 'types/modalTypes';

const pageLength = 100;

function getColumnFromId(id: string) {
  const idsList = [
    'Id',
    'CID',
    'Account',
    'Last Updated By',
    'Date Last Updated',
    'Form Status',
    'Form Notes',
  ];
  const index = idsList.findIndex(item => item === id);
  return `${index}`;
}

async function fetchForms({
  requestData,
  pageParam,
  accountId,
}: TVariables): Promise<TResult> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(formsList(accountId), {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchForms, 500);

type TError = ErrorModal;

type TResult = {
  Data: Array<Forms>;
  Draw: number;
  FilteredRecords: number;
  IsAutoRefreshEnabled: boolean;
  TotalRecords: number;
  PageNumber: number;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  requestData: any;
  pageParam: number;
  accountId: number;
};

function formatRequestData(
  accountId: number,
  keywordSearch: string,
  columnsSearch: Record<string, string>,
  sortedColumn: string,
  sortOrder: string,
  sortedColumn1?: string,
  sortOrder1?: string,
): FormData {
  const formData: any = new URLSearchParams();
  formData.append('draw', '1');
  formData.append('columns[0][data]', 'Id');
  formData.append('columns[0][name]', '');
  formData.append('columns[0][searchable]', 'true');
  formData.append('columns[0][orderable]', 'true');
  formData.append(
    'columns[0][search][value]',
    columnsSearch && columnsSearch['Id'] ? columnsSearch['Id'] : '',
  );
  formData.append('columns[0][search][regex]', 'false');

  formData.append('columns[1][data]', 'Account.CID');
  formData.append('columns[1][name]', '');
  formData.append('columns[1][searchable]', 'true');
  formData.append('columns[1][orderable]', 'true');
  formData.append(
    'columns[1][search][value]',
    columnsSearch && columnsSearch['CID'] ? columnsSearch['CID'] : '',
  );
  formData.append('columns[1][search][regex]', 'false');
  formData.append('columns[2][data]', 'Account.Name');
  formData.append('columns[2][name]', '');
  formData.append('columns[2][searchable]', 'true');
  formData.append('columns[2][orderable]', 'true');
  formData.append(
    'columns[2][search][value]',
    columnsSearch && columnsSearch['Account'] ? columnsSearch['Account'] : '',
  );
  formData.append('columns[2][search][regex]', 'false');
  formData.append('columns[3][data]', 'LastModifiedBy');
  formData.append('columns[3][name]', '');
  formData.append('columns[3][searchable]', 'true');
  formData.append('columns[3][orderable]', 'true');
  formData.append(
    'columns[3][search][value]',
    columnsSearch && columnsSearch['Last Updated By']
      ? columnsSearch['Last Updated By']
      : '',
  );
  formData.append('columns[3][search][regex]', 'false');
  formData.append('columns[4][data]', 'LastModified');
  formData.append('columns[4][name]', 'LastModified');
  formData.append('columns[4][searchable]', 'true');
  formData.append('columns[4][orderable]', 'true');
  formData.append(
    'columns[4][search][value]',
    columnsSearch && columnsSearch['Date Last Updated']
      ? columnsSearch['Date Last Updated']
      : '',
  );
  formData.append('columns[4][search][regex]', 'false');
  formData.append('columns[5][data]', 'SurveyStatus.Description');
  formData.append('columns[5][name]', '');
  formData.append('columns[5][searchable]', 'true');
  formData.append('columns[5][orderable]', 'true');
  formData.append(
    'columns[5][search][value]',
    columnsSearch && columnsSearch['Form Status']
      ? columnsSearch['Form Status']
      : '',
  );
  formData.append('columns[5][search][regex]', 'false');
  formData.append('columns[6][data]', 'Notes');
  formData.append('columns[6][name]', '');
  formData.append('columns[6][searchable]', 'true');
  formData.append('columns[6][orderable]', 'true');
  formData.append(
    'columns[6][search][value]',
    columnsSearch && columnsSearch['Form Notes']
      ? columnsSearch['Form Notes']
      : '',
  );
  formData.append('columns[6][search][regex]', 'false');

  if (sortedColumn1 && sortOrder1) {
    formData.append('order[0][column]', sortedColumn1);
    formData.append('order[0][dir]', sortOrder1);
    formData.append('order[1][column]', sortedColumn);
    formData.append('order[1][dir]', sortOrder);
  } else {
    formData.append('order[0][column]', sortedColumn);
    formData.append('order[0][dir]', sortOrder);
  }
  formData.append('search[value]', `${keywordSearch}`);
  formData.append('search[regex]', 'false');
  formData.append('length', `${100}`);
  formData.append('accountId', `${accountId}`);
  formData.append('timezoneOffset', '-330');
  return formData;
}

function useGetFormsList({
  accountId,
  keywordSearch,
  columnsSearch,
  sortItem,
}: {
  accountId: number;
  keywordSearch: string;
  columnsSearch: Record<string, string>;
  sortItem: SortItemType | Array<SortItemType>;
}): UseInfiniteQueryResult<TResult, TError> {
  let requestData = {};
  if (accountId !== 0) {
    if (sortItem instanceof Array) {
      requestData = formatRequestData(
        accountId,
        keywordSearch,
        columnsSearch,
        getColumnFromId(sortItem[0].sortKey),
        sortItem[0].sortOrder,
        getColumnFromId(sortItem[1].sortKey),
        sortItem[1].sortOrder,
      );
    } else if (sortItem.sortKey && sortItem.sortOrder) {
      requestData = formatRequestData(
        accountId,
        keywordSearch,
        columnsSearch,
        getColumnFromId(sortItem.sortKey),
        sortItem.sortOrder,
      );
    }
  }
  return useInfiniteQuery(
    ['forms_list', keywordSearch, columnsSearch, sortItem, accountId],
    ({ pageParam = 0 }) =>
      debouncedFetch({ requestData, pageParam, accountId }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetFormsList };
